import { useState, useEffect, useCallback, useRef } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import { terraProxyApiClient } from '../clients/testerClient';
import {
  REPORT_GENERATION_SSE_EVENT,
  type ReportGenerationStatus,
} from '@terra/shared-contracts-reports';

export type ReportGenerationJob = {
  id: string;
  status: ReportGenerationStatus;
  progress: number;
  message: string;
  reportId?: string;
  error?: string;
};

type GenerateReportOptions = {
  applicationId: string;
  findingIds: string[];
  title?: string;
  isNewReport?: boolean;
  reportId?: string;
};

type UseReportGenerationReturn = {
  currentJob: ReportGenerationJob | null;
  isGenerating: boolean;
  generateReport: (options: GenerateReportOptions) => Promise<string | null>;
  resetJob: () => void;
};

export function useReportGeneration(): UseReportGenerationReturn {
  const { subscribe } = useSSE();
  const [currentJob, setCurrentJob] = useState<ReportGenerationJob | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const generateReport = useCallback(
    async (options: GenerateReportOptions): Promise<string | null> => {
      try {
        setIsGenerating(true);
        setCurrentJob({
          id: '',
          status: 'PENDING',
          progress: 0,
          message: 'Starting report generation...',
        });

        // Make API call first to get the authoritative jobId
        const response = await terraProxyApiClient.generateReportWithEnrichment(
          {
            findings: options.findingIds,
            applicationId: options.applicationId,
            title: options.title,
            isNewReport: options.isNewReport ?? true,
            reportId: options.reportId,
          },
        );

        // tenantAwareApiWrapper returns the body directly for successful responses (200, 201, 202, 204)
        // or undefined for failures
        if (!response?.jobId) {
          throw new Error('Failed to start report generation');
        }

        const jobId = response.jobId;

        // Update job with the actual jobId
        setCurrentJob({
          id: jobId,
          status: 'PENDING',
          progress: 0,
          message: 'Starting report generation...',
        });

        // Subscribe to SSE updates using the returned jobId
        // The message broker adds enough latency that we won't miss early messages
        unsubscribeRef.current?.();
        unsubscribeRef.current = subscribe(
          REPORT_GENERATION_SSE_EVENT,
          (sseData) => {
            if (sseData.jobId !== jobId) return;

            setCurrentJob((prev) => ({
              ...prev,
              id: sseData.jobId,
              status: sseData.status,
              progress: sseData.progress,
              message: sseData.message,
              reportId: sseData.reportId,
              error: sseData.error,
            }));

            if (sseData.status === 'COMPLETED' || sseData.status === 'FAILED') {
              setIsGenerating(false);
            }
          },
        );

        return jobId;
      } catch (error) {
        console.error('Failed to start report generation:', error);
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;
        setCurrentJob({
          id: '',
          status: 'FAILED',
          progress: 0,
          message: 'Failed to start report generation',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        setIsGenerating(false);
        return null;
      }
    },
    [subscribe],
  );

  const resetJob = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    setCurrentJob(null);
    setIsGenerating(false);
  }, []);

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
    };
  }, []);

  return {
    currentJob,
    isGenerating,
    generateReport,
    resetJob,
  };
}
